// The pictures Codex makes in the chat with OpenAI's image tool, and which
// theme wears each. The board keeps every picture as it comes (run.ts), and
// the chat remembers the last few with the conversation. A theme wears a
// picture Codex names, by OpenAI's id for it or the key the board gave it, or,
// naming none, the one made for it: never one made for something else, and
// never one put on a theme Codex only asked to change.

/** A picture Codex made, as the chat remembers it. */
export interface MadePicture {
  /** the board's key for it, which create_theme takes in backgrounds */
  key: string;
  /** OpenAI's id for the image_generation_call that made it */
  id?: string;
  /** what it shows, in the words it was made from, cut short */
  what?: string;
  /** messages the user has written since the one it was made in: 0 while that message is answered */
  age: number;
  /** a theme wears it, so no theme takes it again unless it names it */
  worn?: boolean;
}

/** How many pictures the chat remembers, the newest: enough for a conversation's themes, and small to save. */
export const KEEP_PICTURES = 8;

/** What Codex may write for a picture it made without naming one the board knows: an id of OpenAI's, "generated", "the picture". */
const MADE = /^(ig_|image_generation|generated|the picture|my picture)/i;

/** What a picture shows, from the words it was made from, short enough to tell Codex in a line. */
export function gist(words: unknown): string | undefined {
  const s = typeof words === "string" ? words.replace(/\s+/g, " ").trim() : "";
  if (!s) return undefined;
  return s.length > 80 ? s.slice(0, 79).trimEnd() + "…" : s;
}

/** A new picture, after the ones before it. The same picture made again (the board keeps one copy) moves to the end, waiting again. */
export function remember(pictures: MadePicture[], p: MadePicture): MadePicture[] {
  return [...pictures.filter((x) => x.key !== p.key), p].slice(-KEEP_PICTURES);
}

/** The user wrote a new message: every picture is a message older. Try again and Keep going carry on the same message, so they never age one. */
export const older = (pictures: MadePicture[]): MadePicture[] => pictures.map((p) => ({ ...p, age: p.age + 1 }));

/** The pictures waiting for a theme: made in the message being answered or the one before, and worn by none. */
export const waiting = (pictures: MadePicture[]): MadePicture[] => pictures.filter((p) => !p.worn && p.age <= 1);

/**
 * The picture made for a theme that names none: the first one made in this
 * message that no theme wears, so two pictures and two themes in one reply
 * pair in the order they were made; else the first left from the message
 * before, for "yes, make it the theme". Never an older one.
 */
function madeFor(pictures: MadePicture[], taken: string[]): MadePicture | undefined {
  const free = waiting(pictures).filter((p) => !taken.includes(p.key));
  return free.find((p) => p.age === 0) ?? free[0];
}

/** A picture as Codex hears of it: what it shows, and the key that names it. */
const told = (p: MadePicture) => (p.what ? `“${p.what}”, ${p.key}` : p.key);

export interface Wearing {
  /** create_theme as the board runs it: each picture Codex made, by the board's key for it */
  input: Record<string, unknown>;
  /** the pictures Codex made that the theme wears */
  keys: string[];
}

/**
 * create_theme after Codex made pictures, as the board runs it. A picture
 * Codex names, by OpenAI's id for it or by the board's key, is that picture,
 * and no one is asked to allow a download of it. Words for a picture it made
 * that name none the board knows ("generated", an id it never saw) are the
 * picture made for the theme. A new theme that names no picture and has no
 * drawing wears the one made for it; a change to a theme (replace), and a
 * theme given backgrounds of its own or none ([]), wear only what they name.
 * Null when the call goes as it is; an error when it asks for a picture Codex
 * made and none is there.
 */
export function wearing(pictures: MadePicture[], input: Record<string, unknown>): Wearing | { error: string } | null {
  const theme = input.theme && typeof input.theme === "object" && !Array.isArray(input.theme) ? (input.theme as Record<string, unknown>) : null;
  if (!theme || !pictures.length) return null;
  const keys: string[] = [];
  let missing = false;
  let backgrounds: unknown[] | undefined;
  if (Array.isArray(theme.backgrounds)) {
    backgrounds = [];
    for (const b of theme.backgrounds) {
      // read the way the board reads an entry: trimmed text, or an object's key
      const word = typeof b === "string" ? b.trim() : b && typeof b === "object" && typeof (b as { key?: unknown }).key === "string" ? (b as { key: string }).key.trim() : "";
      const p = (word ? pictures.find((x) => x.key === word || x.id === word) : undefined) ?? (MADE.test(word) ? madeFor(pictures, keys) : undefined);
      if (p) {
        keys.push(p.key);
        backgrounds.push(p.key);
      } else if (MADE.test(word)) missing = true;
      else backgrounds.push(b);
    }
  } else if (!input.replace && !theme.drawing) {
    const p = madeFor(pictures, keys);
    if (p) {
      keys.push(p.key);
      backgrounds = [p.key];
    }
  }
  if (missing && !backgrounds?.length && !theme.drawing)
    return { error: "No picture you made is waiting for this theme, so it was not made. Make the picture with your image tool, or pass the key the board gave a picture you made in backgrounds." };
  if (!keys.length && !missing) return null;
  return { input: { ...input, theme: { ...theme, backgrounds } }, keys };
}

/**
 * What Codex hears after making a theme while it has pictures it made: which
 * the theme wears and which still wait, each by the key that names it, so it
 * can put another on with replace. Empty when there is nothing to tell.
 */
export function wearingNote(pictures: MadePicture[], keys: string[]): string {
  const worn = pictures.filter((p) => keys.includes(p.key));
  const left = waiting(pictures).filter((p) => !keys.includes(p.key));
  if (!worn.length && !left.length) return "";
  return [
    worn.length ? ` It wears the picture${worn.length > 1 ? "s" : ""} you made: ${worn.map(told).join("; ")}.` : "",
    left.length ? ` Still waiting for a theme: ${left.map(told).join("; ")}.` : "",
    " To put another picture you made on a theme, call create_theme with replace and the picture's key in backgrounds.",
  ].join("");
}

/** What the user's next message tells Codex of the pictures waiting for a theme, so it can name one. Nothing when none waits. */
export function waitingNote(pictures: MadePicture[]): string[] {
  const w = waiting(pictures);
  if (!w.length) return [];
  return [`Pictures you made that wait for a theme, in the order you made them: ${w.map(told).join("; ")}. A new theme that names none wears the first; to choose, pass a picture's key in create_theme's backgrounds.`];
}
